import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

export interface ScanbackBillingDefault {
  bannerCode: string;
  retailItemNumber: number;
  supplierNumber: number;
  fuelperkFlag: string;
  businessUnit?: string | null;
  apVendor?: string | null;
  addressSequence?: number | null;
  supportTeamMember?: string | null;
  entryType?: string | null;
  invoiceDescription?: string | null;
}

export interface ScanbackBillingDefaultWriteRequest {
  bannerCode: string;
  retailItemNumber: number;
  supplierNumber: number;
  fuelperkFlag: string;
  businessUnit?: string | null;
  apVendor?: string | null;
  addressSequence?: number | null;
  supportTeamMember?: string | null;
  entryType?: string | null;
  invoiceDescription?: string | null;
}

interface LookupParams {
  bannerCode: string;
  retailItemNumber: number;
  priceGroupCode: number;
  supplierNumber?: number | null;
}

const ROUTE = "/dm/V1/scanback-billing-defaults";

/**
 * Audit item #5 — look up the default scanback billing row for
 * (banner, retail item, price-group, supplier). The legacy lookup forces
 * supplier=0 when price group != 5 (DSD), so the SPA does not need to pre-zero.
 */
export const useScanbackBillingDefault = (params: LookupParams | undefined) =>
  useQuery({
    queryKey: ["scanback-billing-default", params],
    enabled:
      !!params &&
      !!params.bannerCode &&
      params.retailItemNumber > 0,
    queryFn: async (): Promise<ScanbackBillingDefault | null> => {
      try {
        const response = await apiClient.get<ScanbackBillingDefault>(ROUTE, {
          params: {
            bannerCode: params!.bannerCode,
            retailItemNumber: params!.retailItemNumber,
            priceGroupCode: params!.priceGroupCode,
            supplierNumber: params!.supplierNumber ?? 0,
          },
        });
        return response.data;
      } catch (e: unknown) {
        // 404 = no row yet, which the legacy UI treats as "blank defaults".
        if (
          typeof e === "object" &&
          e !== null &&
          "response" in e &&
          (e as { response?: { status?: number } }).response?.status === 404
        ) {
          return null;
        }
        throw e;
      }
    },
  });

export const useUpsertScanbackBillingDefault = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: ScanbackBillingDefaultWriteRequest
    ): Promise<ScanbackBillingDefault> => {
      const response = await apiClient.put<ScanbackBillingDefault>(ROUTE, body);
      return response.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["scanback-billing-default"] }),
  });
};

export const useDeleteScanbackBillingDefault = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      bannerCode: string;
      retailItemNumber: number;
      supplierNumber: number;
      fuelperkFlag: string;
    }): Promise<void> => {
      await apiClient.delete(ROUTE, { params });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["scanback-billing-default"] }),
  });
};

/**
 * Audit item #23 — BindAddSequence AP-vendor cascade. Drives the address-
 * sequence drop-down once the AP vendor is chosen.
 */
export interface ApVendorAddressSequence {
  addressSequence: number;
  address: string;
  displayText: string;
}

export const useApVendorAddressSequences = (
  apVendorNumber: string | undefined
) =>
  useQuery({
    queryKey: ["ap-vendor-address-sequences", apVendorNumber],
    enabled: !!apVendorNumber && apVendorNumber.length > 0,
    queryFn: async (): Promise<ApVendorAddressSequence[]> => {
      const response = await apiClient.get<{
        apVendorNumber: string;
        items: ApVendorAddressSequence[];
        total: number;
      }>("/dm/V1/lookups/ap-vendor-address-sequences", {
        params: { apVendorNumber },
      });
      return response.data.items ?? [];
    },
  });
